// The lookup confirmation gate: a lookup no longer fires an agent turn on its
// own. It opens the result panel — which answers in well under a second — and
// asks whether to investigate. Only a confirmation, or a real instruction,
// costs an agent turn.
//
// Everything decidable lives here as a pure function, table-tested, for the
// reason `tabIntent` does: the bugs in this kind of code live in the ORDER of
// the branches, not inside them.
import { extractSingleIp } from './utils/detectIp';
import { extractSingleDomain } from './utils/detectDomain';
import { isAirqlQuery } from '@/services/airql';

/** How the user named the thing they want looked up. Decides the offer's
 *  wording and, at the call site, which panel tab opens. */
export type LookupKind = 'ip' | 'domain' | 'search';

/**
 * One lookup the user entered and the question Elliot asked back — an exchange
 * the agent was never told about.
 *
 * These are the conversation's history as much as any message is, so they are
 * never thrown away: they stop being answerable, and that is all. The flags say
 * which of those states it is in, and `at` says where in the conversation it
 * belongs.
 */
export interface Staged {
  /** What the agent would receive: the bare indicator, or the AirQL query. */
  query: string;
  kind: LookupKind;
  /** The user said no. Not answerable any more, still history, and still
   *  context — "check it after all" has to know what "it" was. */
  declined?: boolean;
  /** Its context has now reached the agent, inside a turn's hidden note. Not
   *  answerable, not repeated in a later note, and still on screen: it is what
   *  the user saw happen. */
  sent?: boolean;
  /** How many real messages came before it. That is its position in the flow,
   *  and it survives everything: the server's transcript holds exactly those
   *  messages, in that order, so the exchange lands back between the right two
   *  after a reload. */
  at: number;
  /**
   * The draw that decides how this one question arrives — see `offerPacing`.
   * A float in [0, 1), taken ONCE when the offer is created and carried from
   * there, which is the only reason `Math.random()` is safe here: drawing during
   * a render would re-roll on every one and change the pace mid-word.
   *
   * Belongs to the offer rather than to the query so that checking 8.8.8.8 twice
   * is two different waits. Deriving it from the query — which is what this used
   * to do — made a lookup take the same milliseconds forever, and nothing on a
   * network does that.
   *
   * Optional because offers written to storage before it existed come back
   * without one; `offerSeed` falls those back to the old query-derived pace.
   */
  seed?: number;
}

/**
 * The ways Elliot can ask. One sentence every time reads like a form field; the
 * variety is what makes the offer feel like it came from someone.
 *
 * All question-shaped on purpose, because both answers have to fit: the buttons
 * AND a typed "yes". `{subject}` is the user's own indicator.
 *
 * The OPENINGS are spread deliberately, not just the sentences. Sixteen of the
 * original twenty began "Want me to", which gave a four-in-five chance per
 * lookup — so six in a row opening identically was the likely outcome, and it
 * happened: a column of questions that were technically all different and read
 * as one sentence repeated. It is also the worst place for a repeat, because the
 * type-out spends its first third spelling out those same words before anything
 * distinguishing appears.
 */
const OFFERS = [
  'Want me to dig deeper on {subject}?',
  'Should I take a closer look at {subject}?',
  'Should I dig into {subject}?',
  'Want the full picture on {subject}?',
  'Want me to look into {subject}?',
  'Should I run a full check on {subject}?',
  'Should I pull everything on {subject}?',
  'Want a full workup on {subject}?',
  'Want me to investigate {subject}?',
  'Want me to go deeper on {subject}?',
  'Should I check what {subject} is connected to?',
  'Curious what {subject} has been up to?',
  "Should I pull {subject}'s history?",
  'Worth going past the profile on {subject}?',
  'Should I check {subject} against the rest of our data?',
  'Want to see what else is on {subject}?',
  'Want me to check {subject} properly?',
  'Want me to keep going on {subject}?',
  'Should I review {subject} in full?',
  'Want me to check {subject} further?',
] as const;

// An AirQL query is not a "who" and has no history or profile of its own, so
// five of the twenty read wrong with "these results" in the subject slot. The
// search pool is the rest; the user never learns there were more.
const SEARCH_SUBJECT = 'these results';
const SEARCH_POOL = OFFERS.filter(
  (t) => !/is connected to|has been up to|'s history|the profile on|what else is on/.test(t),
);

/**
 * Pick the sentence for one exchange, and put the subject in it.
 *
 * Chosen from the QUERY rather than at random, which is what makes it hold
 * still: the same lookup always reads the same way, so the sentence does not
 * change under a re-render, a reload, or the trip through the timeline — and
 * two different indicators still get two different sentences, which is the
 * variety we were after. (A random draw would have needed storing beside the
 * offer to stop it re-rolling on every render.)
 *
 * One place, so the bubble on screen and anything else that describes the
 * exchange can never drift apart.
 */
export function offerText(s: Staged): string {
  const pool = s.kind === 'search' ? SEARCH_POOL : OFFERS;
  const subject = s.kind === 'search' ? SEARCH_SUBJECT : s.query;
  return pool[queryHash(s.query) % pool.length].replace('{subject}', subject);
}

/** djb2 over the query, non-negative. Everything about how an offer PRESENTS
 *  itself is derived from this, so one lookup always looks and reads the same. */
function queryHash(query: string): number {
  let h = 5381;
  for (let i = 0; i < query.length; i += 1) h = ((h << 5) + h + query.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * The draw that paces one offer, and the fallback for offers that predate it.
 *
 * A number outside [0, 1) cannot have come from `Math.random()`, so it came from
 * a hand-edited or corrupted `localStorage` entry. Storage is not ours to trust:
 * a NaN here would propagate into `charMs` and hang the question half-typed,
 * with no buttons under it.
 */
function offerSeed(s: Staged): number {
  const { seed } = s;
  if (typeof seed === 'number' && Number.isFinite(seed) && seed >= 0 && seed < 1) return seed;
  return queryHash(s.query) / 2 ** 31; // the pace this offer had before seeds existed
}

/** mulberry32, seeded from the offer's own draw. Both numbers the pacing needs
 *  come from here, so one offer plays one way for its whole life however many
 *  times it re-renders — the property the old query-hash had, without tying the
 *  pace to the query itself. */
function rng(seed: number): () => number {
  let a = (seed * 2 ** 32) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 2 ** 32;
  };
}

/**
 * How long this offer takes to arrive: a pause, then a per-character type-out.
 *
 * The question is answered before it is asked — nothing was sent anywhere, the
 * sentence is picked locally, and it could render in the same frame the user
 * pressed Enter. That is exactly the problem: an instant reply to "look at
 * 6.6.6.6" reads as a form validating a field, not as someone deciding the
 * lookup is worth a turn. This is FAKED latency.
 *
 * The type-out is EVEN, one character at a time. A version of this delivered the
 * sentence in bursts of two or three words, on the reasoning that a real stream
 * arrives as tokens and never as letters — which is true of a network and wrong
 * on screen. Watched rather than reasoned about, word-bursts read as a component
 * re-rendering, and the steady character march is the part that reads as someone
 * writing. Realism lives in the two draws below; it does not get to spend the
 * animation.
 *
 * Both draws are CUBED, which is the whole difference from a plain range. Real
 * latency piles up near a floor and keeps a long tail — most lookups quick, one
 * in ten noticeably slow — and it is the occasional slow one that makes the
 * other nine read as a wait rather than a fixed animation. Drawn evenly, the
 * spread reads as a randomiser walking a slider.
 *
 * `thinkMs` is time-to-first-token, not decoration: the sentence is already
 * decided, so the loader is the only part of this modelling a request at all.
 *
 * Both come from the offer's own `seed`, drawn once at creation. So the pace is
 * fixed for this offer's life — a re-render cannot change it mid-word, which is
 * what the query hash used to buy us — but two lookups of the SAME indicator are
 * two different waits, as they would be if either had actually left the machine.
 *
 * Per-character speed is a TOTAL typing time divided by the length, never a
 * fixed ms-per-character: fixed-per-character lets the sentence's own length
 * decide the wait, and the twenty phrasings run 26 to 57 characters.
 */
export function offerPacing(
  offer: Staged,
  length: number,
): { thinkMs: number; charMs: number } {
  const r = rng(offerSeed(offer));
  // SQUARED, where the type-out below is cubed, and the difference is the point.
  // Cubing pins most draws on the floor — with a 220ms floor that put 55% of
  // pauses under 400ms, which is not a pause, it is a frame. The wait before the
  // first character is the half meant to be FELT, so its spread sits in the range
  // where it can be: nothing under half a second, typically nearer nine tenths.
  const thinkMs = Math.round(480 + r() ** 2 * 1500); // 0.48–1.98s, median ~0.87s
  const typeMs = 550 + r() ** 3 * 1700; // ~0.75s typical, 2.25s tail
  return { thinkMs, charMs: Math.max(8, Math.round(typeMs / Math.max(1, length))) };
}

/** Can this exchange still be answered with a yes or a no? Declining ends that,
 *  and so does a turn having carried its context to the agent. */
export function answerable(s: Staged): boolean {
  return !s.declined && !s.sent;
}

/**
 * Index of the one exchange a yes or a no can be about, or -1 when there is
 * none.
 *
 * Only ever the LAST one, and only while it is still answerable. Entering
 * another indicator supersedes the question before it — the user moved on
 * without answering, and a question they have already walked past must not come
 * back. Searching backwards for the newest ANSWERABLE one did exactly that:
 * declining the newest walked the buttons back onto an older exchange, so "no"
 * looked like it had re-asked about a previous IP. Answering the last one
 * therefore ends it: there is nothing behind it to fall through to.
 */
export function liveOfferAt(staged: Staged[]): number {
  const last = staged.length - 1;
  return last >= 0 && answerable(staged[last]) ? last : -1;
}

/** What the user's message means while an offer is on screen. */
export type Reply = 'yes' | 'no' | 'other';

// Whole-message answers only, English and Hebrew — the user types in both. A
// short listed set on purpose: anything not here is 'other', which is never a
// rejection, only a different route (it becomes the agent turn, carrying the
// offer as context). That is what keeps "yes, and pull related IOCs" from being
// swallowed as a bare yes and losing the rest of the sentence.
const YES = new Set([
  'y', 'yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'k',
  'go', 'go ahead', 'do it', 'please', 'please do', 'yes please',
  'כן', 'כן בבקשה', 'בטח', 'קדימה', 'סבבה', 'אישור', 'תחפור', 'בהחלט', 'אוקיי', 'אוקי',
]);
const NO = new Set([
  'n', 'no', 'nope', 'nah', 'no thanks', 'cancel', 'skip', 'never mind', 'nevermind',
  'לא', 'לא תודה', 'עזוב', 'ביטול', 'לא צריך', 'לא עכשיו',
]);

/**
 * Classify a whole message as an answer to an offer.
 *
 * Trailing punctuation and case are ignored ("Yes!", "כן." are answers); inner
 * spacing is collapsed so "go  ahead" matches. Nothing is stemmed or fuzzy
 * matched — a near-miss becoming a silent yes would send a turn the user did
 * not ask for, while a near-miss becoming 'other' merely sends their words to
 * the agent, which is what they typed them for.
 */
export function classifyReply(text: string): Reply {
  const t = text
    .trim()
    .toLowerCase()
    .replace(/[.!?,;:…]+$/u, '')
    .replace(/\s+/gu, ' ')
    .trim();
  if (!t) return 'other';
  if (YES.has(t)) return 'yes';
  if (NO.has(t)) return 'no';
  return 'other';
}

/**
 * Is this message a lookup? Only if the WHOLE message is one — the detectors
 * are anchored, so "is 8.8.8.8 malicious?" is a question for the agent and
 * reaches it untouched.
 *
 * `/ip ` and `/domain ` are already stripped by the prompt (see `composeSend`),
 * so a command arrives here as the bare indicator. `/search ` survives and is
 * stripped here. `/scan` deliberately matches nothing: a live scan IS an agent
 * action, so there is nothing to offer.
 */
export function detectLookup(text: string): { query: string; kind: LookupKind } | null {
  const t = text.trim();
  if (/^\/search\b/i.test(t)) {
    const query = t.replace(/^\/search\s*/i, '').trim();
    // A bare `/search` is a no-op, exactly as before the gate: it must never
    // reach the agent, and there is nothing to offer either.
    return query ? { query, kind: 'search' } : null;
  }
  // IP first. An IPv4 never validates as a domain, but the order keeps the
  // intent explicit — same as the panel-opening path in HomePage.
  const ip = extractSingleIp(t);
  if (ip) return { query: ip, kind: 'ip' };
  const domain = extractSingleDomain(t);
  if (domain) return { query: domain, kind: 'domain' };
  // A bare AirQL query — `keyword:value …` with no `/search` in front — is a
  // lookup just the same: the composer's `/search` sends exactly this string, and
  // typing it without the prefix used to skip the gate and spend an agent turn
  // (plus an auto-opened panel) where `/search` only offers. So it gates like
  // `/search` does. `isAirqlQuery` is the server's own fork between "run the
  // query" and "read it as prose", so matching it here routes the message the way
  // its answer will come. Single IPs/domains are caught above and never reach
  // this — they carry no `:` for `isAirqlQuery` to key on.
  return isAirqlQuery(t) ? { query: t, kind: 'search' } : null;
}

/** What sending this message should do, given the offers already on screen. */
export type SendIntent =
  | { act: 'offer'; query: string; kind: LookupKind } // stage it and ask
  | { act: 'confirm' } // yes → investigate the live offer
  | { act: 'decline' } // no → drop its buttons, keep it as context
  | { act: 'turn'; text: string }; // a real agent turn

/**
 * The gate's whole policy, as one decision.
 *
 * A yes or a no only counts while a LIVE offer is on screen — staged and not
 * declined. With nothing to answer, "yes" is just a word the user typed and
 * goes to the agent as one; reading it as a confirmation would fire a turn
 * against whatever was staged hours ago.
 *
 * Another indicator while an offer is open stages too, rather than replacing
 * it: that is what lets a later "find out whether they are connected" reach the
 * agent knowing what "they" means.
 */
export function sendIntent(o: { text: string; staged: Staged[] }): SendIntent {
  const t = o.text.trim();
  // The same one the buttons are on — a typed yes and a click must mean the
  // same thing, or a "yes" could answer a question no longer on screen.
  if (liveOfferAt(o.staged) !== -1) {
    const reply = classifyReply(t);
    if (reply === 'yes') return { act: 'confirm' };
    if (reply === 'no') return { act: 'decline' };
  }
  const lookup = detectLookup(t);
  return lookup ? { act: 'offer', ...lookup } : { act: 'turn', text: t };
}

/** A rendered offer: the whole exchange, both bubbles. */
export interface OfferRow {
  offer: Staged;
  /** True for the one offer that can still be answered — the newest one that has
   *  not been declined. Only it draws buttons: several live sets of Yes/No
   *  competing for one answer is a question the user cannot answer. */
  live: boolean;
}

/**
 * The conversation to render: the real transcript with the gate's exchanges
 * spliced back into the places they happened.
 *
 * An offer row carries the WHOLE exchange — the user's indicator and Elliot's
 * question. That is not a shortcut, it is the only thing that is true: neither
 * bubble was ever sent anywhere, so the server's transcript has no row for
 * either, and a message list rebuilt from that transcript cannot hold half of
 * one. Owning both is what makes them render identically live, after a reload,
 * and in a conversation whose transcript is still empty because nothing has ever
 * been asked in it.
 *
 * Position comes from `at`, a count of REAL messages — which is why it still
 * points at the right gap after a reload, when `messages` has been rebuilt from
 * the server and the offers from this browser. Appending them at the end instead
 * is what erased the history of a conversation the moment a question was asked
 * in it: the exchanges jumped below the answer, or vanished with the turn.
 */
export function rowsWithOffers<T>(messages: T[], staged: Staged[]): (T | OfferRow)[] {
  if (staged.length === 0) return messages;
  const liveAt = liveOfferAt(staged);
  const rows: (T | OfferRow)[] = [];
  let next = 0;
  // `staged` is append-only and `at` never decreases, so one pass places them all.
  const emitThrough = (count: number) => {
    while (next < staged.length && staged[next].at <= count) {
      rows.push({ offer: staged[next], live: next === liveAt });
      next += 1;
    }
  };
  for (let i = 0; i < messages.length; i += 1) {
    emitThrough(i); // everything staged while only `i` messages existed
    rows.push(messages[i]);
  }
  emitThrough(staged.length ? staged[staged.length - 1].at : 0); // the trailing ones
  return rows;
}

/**
 * The marker the note starts with. The server cuts the message here when it
 * reconstructs the transcript, so the user's bubble shows only what they typed
 * — live and after a refresh. Two notes already work this way (the upload note
 * and the insight block); keep this literal in step with
 * `settings.lookup_note_marker` on the server.
 */
export const LOOKUP_NOTE_MARKER = '[Earlier in this conversation';

/**
 * The hidden note that carries the offers the agent never saw, as a plain
 * transcript of them. Self-describing on purpose: the agent needs no prompt
 * change to read it, and a human reading the logs needs no key either.
 *
 * Empty string when there is nothing to carry — the common single-offer yes,
 * which then sends byte-for-byte what `/ip` sent before the gate existed.
 */
export function lookupNote(entries: Staged[], opts: { confirmed?: boolean } = {}): string {
  const unseen = entries.filter((s) => !s.sent);
  if (unseen.length === 0) return '';
  // One line per indicator. The same one can legitimately be staged twice (asked
  // about, walked past, then raised again), and repeating it in the note tells
  // the agent nothing it does not already have on the line above.
  const seen = new Set<string>();
  const once = unseen.filter((s) => !seen.has(s.query) && (seen.add(s.query), true));
  // What HAPPENED, not the words it happened in. Quoting `offerText` here put a
  // sentence in the agent's mouth that it never said — and it learned it:
  // measured across real sessions, 4 of 4 gated ones ended with the agent asking
  // "Want me to dig deeper on X?" itself, against 1 of 4 before the gate existed,
  // on the same kind of task. Its own offer has no buttons, so the screen ended
  // up with two identical-looking questions, only one of them answerable — and
  // in one session it re-opened a lookup the user had just declined. Describing
  // the act removes the phrasing that taught it, with nothing to counteract in
  // the agent's prompt (which stays untouched by this feature).
  const lines = once.flatMap((s) => [
    // As the user entered it. `/search` is kept because without it an AirQL
    // query reads as an indicator: `port:22` sat in the note looking like
    // something to look up, and the agent had to infer from context that it was
    // a query (measured: it did, and silently excluded it from its count).
    s.kind === 'search' ? `user: /search ${s.query}` : `user: ${s.query}`,
    'you: (offered to investigate it)',
  ]);
  // The last line has to be true of the message the note is riding on, and
  // "their reply" is not. Two ways it was wrong, both found in real sessions:
  // on a CONFIRMATION the message is a bare indicator, which reads as a
  // correction of one of the exchanges rather than the thing to go and do; and
  // on a plain turn the user may have declined and then asked about something
  // else entirely (measured: a file hash, after declining a domain), which is a
  // reply to nothing. So state the two facts and let the agent connect them.
  const closing = opts.confirmed
    ? 'The message above is the lookup the user chose to run. The exchanges' +
      ' listed here they left unanswered — context, not tasks.'
    : 'The message above is what the user asked for. The exchanges listed here' +
      ' are context in the same conversation, which it may refer to.';
  return (
    `\n\n${LOOKUP_NOTE_MARKER} the user entered these lookups and was asked` +
    ` whether to investigate. Shown to them, not previously sent to you:\n` +
    `${lines.join('\n')}\n` +
    `${closing}]`
  );
}
